import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, KeyRound, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";

/**
 * ForgotPasswordPage — Clean Enterprise restyle.
 * Two-step flow: (1) request a reset token by email,
 * (2) enter token + new password. Same auth backend as before — only
 * the visual chrome changes.
 */
export default function ForgotPasswordPage() {
  const [email,           setEmail]           = useState("");
  const [resetToken,      setResetToken]      = useState("");
  const [newPassword,     setNewPassword]     = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [step,            setStep]            = useState(1);   // 1 → email, 2 → token+password
  const [submitted,       setSubmitted]       = useState(false);
  const [loading,         setLoading]         = useState(false);

  const passwordStrength = {
    minLength:    newPassword.length >= 8,
    hasUppercase: /[A-Z]/.test(newPassword),
    hasLowercase: /[a-z]/.test(newPassword),
    hasNumber:    /\d/.test(newPassword),
  };
  const passwordValid = Object.values(passwordStrength).every(Boolean);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Please enter your email"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/forgot-password`, { email });
      setStep(2);
      toast.success("Check your inbox. If registered, a reset token has been sent.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetToken.trim()) { toast.error("Enter the reset token from your email"); return; }
    if (!passwordValid)     { toast.error("Password must meet all requirements"); return; }
    if (newPassword !== confirmPassword) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/reset-password`, {
        email,
        reset_token: resetToken.trim(),
        new_password: newPassword,
      });
      setSubmitted(true);
      toast.success("Password reset successfully.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid or expired token.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-canvas text-ink font-body flex flex-col">
      <MarketingNav />

      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 py-12 sm:py-16">
        <div className="w-full max-w-md">
          {/* Header block */}
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto rounded-full bg-brand-greenPale border border-brand-green/30
                            flex items-center justify-center mb-4">
              <KeyRound className="w-6 h-6 text-brand-green" />
            </div>
            <h1 className="font-display text-3xl sm:text-4xl text-ink leading-tight">
              Reset your password.
            </h1>
            <p className="text-slateline-600 mt-3 text-sm">
              {submitted
                ? "Your password has been reset successfully."
                : step === 1
                  ? "Enter your email to receive a reset token."
                  : "Enter the token from your email and choose a new password."}
            </p>
          </div>

          <div className="bg-white border border-slateline-200 rounded-xl p-6 sm:p-8 shadow-sm">
            {submitted ? (
              <div className="text-center">
                <CheckCircle2 className="w-12 h-12 text-brand-green mx-auto mb-4" />
                <p className="text-slateline-600 mb-6">You can now sign in with your new password.</p>
                <Link to="/login"
                      className="inline-flex items-center justify-center w-full h-11 rounded-md
                                 bg-brand-green hover:bg-brand-greenHover text-white font-semibold text-sm
                                 tracking-wide shadow-sm transition-colors">
                  Go to sign in
                </Link>
              </div>
            ) : step === 1 ? (
              <form onSubmit={handleRequestReset} className="space-y-5">
                <FieldGroup label="Email">
                  <FieldInputWithIcon icon={Mail}>
                    <input
                      id="email" type="email" placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-9 pr-3 h-11 bg-white border border-slateline-200 rounded-md
                                 text-ink placeholder-slateline-400 outline-none
                                 focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors"
                    />
                  </FieldInputWithIcon>
                </FieldGroup>

                <PrimaryButton loading={loading}>Send reset token</PrimaryButton>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-5">
                <div className="rounded-md bg-brand-green/5 border border-brand-green/20 p-3 text-sm text-ink leading-snug">
                  A reset token was sent to <strong>{email}</strong> if that
                  address is registered. Check your inbox (and spam folder)
                  and enter the 8-character token below.
                </div>

                <FieldGroup label="Reset token (from email)">
                  <input
                    id="resetToken" type="text" placeholder="A3BF 92C1"
                    value={resetToken}
                    inputMode="text"
                    autoComplete="one-time-code"
                    autoFocus
                    onChange={(e) =>
                      // Accept anything pasted (with spaces, dashes, lowercase)
                      // and normalise to the 8-char A-Z 2-9 alphabet (P1-10).
                      setResetToken(
                        e.target.value
                          .toUpperCase()
                          .replace(/[^A-Z2-9]/g, "")
                          .slice(0, 8)
                      )
                    }
                    onPaste={(e) => {
                      // Belt-and-braces — onChange already strips, but doing
                      // it on paste means we never even briefly show the raw
                      // pasted value.
                      e.preventDefault();
                      const cleaned = (e.clipboardData?.getData("text") || "")
                        .toUpperCase()
                        .replace(/[^A-Z2-9]/g, "")
                        .slice(0, 8);
                      setResetToken(cleaned);
                    }}
                    className="w-full h-12 bg-white border border-slateline-200 rounded-md
                               text-ink font-mono tracking-[0.45em] text-center text-lg uppercase
                               outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors"
                    maxLength={8}
                  />
                  <p className="text-xs text-slateline-400 mt-1.5">
                    8 characters · A–Z &amp; 2–9 · spaces and dashes are ignored
                  </p>
                </FieldGroup>

                <FieldGroup label="New password">
                  <input
                    id="newPassword" type="password" placeholder="Create a strong password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full h-11 bg-white border border-slateline-200 rounded-md px-3
                               text-ink placeholder-slateline-400 outline-none
                               focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors"
                  />
                  {newPassword && (
                    <div className="mt-2 grid grid-cols-2 gap-1.5 text-xs">
                      <Strength ok={passwordStrength.minLength}    label="8+ characters" />
                      <Strength ok={passwordStrength.hasUppercase} label="1 uppercase" />
                      <Strength ok={passwordStrength.hasLowercase} label="1 lowercase" />
                      <Strength ok={passwordStrength.hasNumber}    label="1 number" />
                    </div>
                  )}
                </FieldGroup>

                <FieldGroup label="Confirm new password">
                  <input
                    id="confirmPassword" type="password" placeholder="Repeat your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={[
                      "w-full h-11 bg-white border rounded-md px-3 text-ink placeholder-slateline-400",
                      "outline-none focus:ring-2 focus:ring-brand-green/20 transition-colors",
                      confirmPassword && newPassword !== confirmPassword
                        ? "border-rose-500 focus:border-rose-500"
                        : "border-slateline-200 focus:border-brand-green",
                    ].join(" ")}
                  />
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-xs text-rose-600 mt-1.5">Passwords do not match</p>
                  )}
                </FieldGroup>

                <PrimaryButton
                  loading={loading}
                  disabled={!passwordValid || newPassword !== confirmPassword}
                >
                  Reset password
                </PrimaryButton>

                <button type="button" onClick={() => setStep(1)}
                        className="w-full text-center text-sm text-slateline-500 hover:text-ink transition-colors">
                  ← Use a different email
                </button>
              </form>
            )}
          </div>

          <p className="text-center text-sm text-slateline-500 mt-6">
            Remember your password?{" "}
            <Link to="/login" className="text-brand-green hover:underline font-medium inline-flex items-center gap-1">
              <ArrowLeft className="w-3.5 h-3.5" /> Sign in
            </Link>
          </p>
        </div>
      </main>

      <MarketingFooter />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form bits
// ---------------------------------------------------------------------------

function FieldGroup({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slateline-500">
        {label}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function FieldInputWithIcon({ icon: Icon, children }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slateline-400" />
      {children}
    </div>
  );
}

function PrimaryButton({ loading, disabled, children }) {
  return (
    <button
      type="submit" disabled={loading || disabled}
      className="w-full inline-flex items-center justify-center h-11 rounded-md
                 bg-brand-green hover:bg-brand-greenHover disabled:opacity-50 disabled:cursor-not-allowed
                 text-white font-semibold text-sm tracking-wide shadow-sm transition-colors"
    >
      {loading ? (
        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
      ) : children}
    </button>
  );
}

function Strength({ ok, label }) {
  return (
    <div className={`flex items-center gap-1.5 ${ok ? "text-brand-green" : "text-slateline-400"}`}>
      <span>{ok ? "✓" : "○"}</span>
      <span>{label}</span>
    </div>
  );
}
